import React, { useMemo } from 'react';
import { ConnectedUser } from '../../../stores/types/connection';
import { Probe } from '../types';
import HealthPanel from '../health-panel';

interface Props {
	probe: Probe<ConnectedUser[]> | undefined;
}

/**
 * Clients as greenroom-socket has persisted them to sync. A row here means the
 * socket service believes that client is attached to `meeting-<roomId>`; a stale
 * row is itself a useful signal, so nothing is filtered out.
 */
export default function ConnectedClientsPanel({ probe }: Props) {
	const users = probe?.data ?? [];

	const counts = useMemo(() => ({
		total: users.length,
		producers: users.filter((user) => user.isProducer).length,
		inRoom: users.filter((user) => user.in_room).length,
		onsite: users.filter((user) => !!user.onsiteLocationSession).length,
	}), [users]);

	return (
		<HealthPanel
			title='Connected clients'
			source='greenroom-socket → sync · connected user entities'
			probe={probe}
		>
			<dl className='health-facts'>
				<dt>Connected</dt>
				<dd>{counts.total}</dd>
				<dt>Producers</dt>
				<dd>{counts.producers}</dd>
				<dt>In room</dt>
				<dd>{counts.inRoom} of {counts.total}</dd>
				{!!counts.onsite && (
					<>
						<dt>Onsite</dt>
						<dd>{counts.onsite}</dd>
					</>
				)}
			</dl>

			<table className='health-table'>
				<thead>
					<tr>
						<th>Client</th>
						<th>Role</th>
						<th>Socket</th>
					</tr>
				</thead>
				<tbody>
					{users.map((user) => (
						<tr key={user.socketId ?? `${user.gr_user}-${user.session}`}>
							<td>
								{user.first_name} {user.last_name}
								<div className='health-subtle'>{user.email}</div>
							</td>
							<td>
								{user.isProducer
									? <span className='health-tag'>producer</span>
									: <span className='health-tag'>presenter</span>}
								{!user.in_room && <span className='health-tag health-tag--warn'>lobby</span>}
								{user.companionMode && <span className='health-tag'>companion</span>}
							</td>
							<td className='health-mono'>{user.socketId ?? '—'}</td>
						</tr>
					))}
				</tbody>
			</table>
		</HealthPanel>
	);
}
